import { useState, useEffect } from 'react'
import { loadQuotesList, loadQuoteById, updateQuoteStatus, updateAdvancePaid } from '../lib/quotes'

const STATUS_OPTIONS = ['Quoted', 'Confirmed', 'In Production', 'Ready', 'Delivered']

export default function LandingScreen({ onNewQuote, onLoadQuote, onAnalytics, onLogout, userEmail }) {
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [loadingId, setLoadingId] = useState(null)
  const [showList, setShowList] = useState(false)
  const [statusUpdating, setStatusUpdating] = useState({}) // id -> true while saving
  const [statusError, setStatusError] = useState({})       // id -> error string
  const [advanceUpdating, setAdvanceUpdating] = useState({}) // id -> true while saving
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    loadQuotesList()
      .then(data => {
        setQuotes(data)
        setShowList(false)
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const filtered = quotes.filter(q => {
    const s = search.toLowerCase()
    return (
      q.customer_name?.toLowerCase().includes(s) ||
      q.display_quote_no?.toLowerCase().includes(s) ||
      q.address?.toLowerCase().includes(s)
    )
  })

  async function handleOpen(quote) {
    setLoadingId(quote.id)
    try {
      const full = await loadQuoteById(quote.id)
      onLoadQuote(full)
    } catch (e) {
      alert('Failed to load quote: ' + e.message)
    } finally {
      setLoadingId(null)
    }
  }

  async function handleAdvanceToggle(q) {
    const newPaid = !q.advance_paid
    setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, advance_paid: newPaid } : x))
    setAdvanceUpdating(s => ({ ...s, [q.id]: true }))
    try {
      await updateAdvancePaid(q.id, newPaid)
    } catch (e) {
      console.error('Failed to update advance paid:', e)
      setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, advance_paid: !newPaid } : x))
    } finally {
      setAdvanceUpdating(s => ({ ...s, [q.id]: false }))
    }
  }

  async function handleStatusChange(q, newStatus) {
    const prevStatus = q.status
    // Optimistic update
    setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, status: newStatus } : x))
    setStatusUpdating(s => ({ ...s, [q.id]: true }))
    setStatusError(s => ({ ...s, [q.id]: null }))
    try {
      await updateQuoteStatus(q.id, newStatus)
    } catch (e) {
      // Revert on failure
      setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, status: prevStatus } : x))
      setStatusError(s => ({ ...s, [q.id]: 'Failed to update' }))
    } finally {
      setStatusUpdating(s => ({ ...s, [q.id]: false }))
    }
  }

  function formatDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    })
  }

  function formatCurrency(n) {
    if (n == null) return ''
    return '\u20b9' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7f3f6',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, sans-serif',
    }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      {!showList && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          position: 'relative',
        }}>
          <div style={{ position: 'absolute', top: 16, right: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#b89ab4' }}>{userEmail}</span>
            <button onClick={onLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#b89ab4', fontFamily: 'inherit', padding: 0, textDecoration: 'underline' }}>Sign out</button>
          </div>
          <img
            src="/CS_Software_DinoLogo.png"
            alt="DrapeIQ"
            style={{ height: 100, width: 'auto', marginBottom: 12 }}
          />
          <p style={{
            fontSize: 12,
            letterSpacing: '0.14em',
            color: '#9b6b8e',
            textTransform: 'uppercase',
            margin: '0 0 56px 0',
          }}>
            Soft Furnishings Quotation Platform
          </p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={onNewQuote}
              style={{
                background: '#9b2d7f',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 20px rgba(155,45,127,0.22)',
              }}
              onMouseOver={e => e.currentTarget.style.background = '#7e2468'}
              onMouseOut={e => e.currentTarget.style.background = '#9b2d7f'}
            >
              <span style={{ fontSize: 20, lineHeight: 1 }}>+</span> New Quote
            </button>

            <button
              onClick={() => setShowList(true)}
              disabled={loading}
              style={{
                background: '#fff',
                color: '#9b2d7f',
                border: '1.5px solid #9b2d7f',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: 15,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: loading ? 0.6 : 1,
              }}
              onMouseOver={e => { if (!loading) e.currentTarget.style.background = '#f7edf5' }}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              <span style={{ fontSize: 16 }}>&#9998;</span>
              {loading
                ? 'Loading...'
                : `Edit Existing${quotes.length > 0 ? ' (' + quotes.length + ')' : ''}`
              }
            </button>

            <button
              onClick={onAnalytics}
              style={{
                background: '#fff',
                color: '#9b2d7f',
                border: '1.5px solid #9b2d7f',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f7edf5'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              <span style={{ fontSize: 16 }}>&#9783;</span> Analytics
            </button>
          </div>

          {error && (
            <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 24 }}>{error}</p>
          )}
          {!loading && quotes.length === 0 && !error && (
            <p style={{ color: '#b89ab4', fontSize: 13, marginTop: 32 }}>
              No quotes saved yet. Create your first one.
            </p>
          )}
        </div>
      )}

      {/* ── Quote list ──────────────────────────────────────── */}
      {showList && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            background: '#fff',
            borderBottom: '1px solid #ede5eb',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}>
            <img src="/CS_Software_DinoLogo.png" alt="DrapeIQ" onClick={() => setShowList(false)} style={{ height: 28, width: 'auto', cursor: 'pointer' }} />
            <span style={{ flex: 1 }} />
            <input
              type="text"
              placeholder="Search customer, quote no, address..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                border: '1px solid #e0cedd',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                color: '#333',
                background: '#faf6f9',
                outline: 'none',
                width: 280,
              }}
            />
            <button
              onClick={onNewQuote}
              style={{
                background: '#9b2d7f',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '9px 20px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + New Quote
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <button
                onClick={() => setShowList(false)}
                style={{
                  background: '#fff',
                  border: 'none',
                  color: '#a07090',
                  fontSize: 12,
                  cursor: 'pointer',
                  letterSpacing: '0.5px',
                  opacity: 0.75,
                  fontFamily: 'inherit',
                  borderRadius: 8,
                  padding: '6px 14px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  display: 'inline-block',
                }}
              >
                ← Back to Home
              </button>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #ede5eb',
              overflow: 'hidden',
            }}>
              {!isMobile && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '170px 1fr 110px 140px 80px 150px 72px',
                  padding: '10px 20px',
                  background: '#faf6f9',
                  borderBottom: '1px solid #ede5eb',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#9b6b8e',
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                }}>
                  <span>Quote No</span>
                  <span>Customer</span>
                  <span>Date</span>
                  <span style={{ textAlign: 'right' }}>Grand Total</span>
                  <span style={{ textAlign: 'center' }}>Advance</span>
                  <span style={{ textAlign: 'center' }}>Status</span>
                  <span></span>
                </div>
              )}

              {filtered.length === 0 && (
                <div style={{ padding: '48px', textAlign: 'center', color: '#bbb', fontSize: 14 }}>
                  No quotes found.
                </div>
              )}

              {filtered.map((q, i) => isMobile ? (
                <div
                  key={q.id}
                  style={{
                    padding: '14px 16px',
                    borderTop: i > 0 ? '1px solid #f5eff3' : 'none',
                    borderLeft: !q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) ? '3px solid #f0a500' : '3px solid transparent',
                    background: !q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) ? '#fff8f0' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 700, color: '#2d1a29' }}>
                        {q.display_quote_no}
                      </span>
                      {q.revision > 0 && (
                        <span style={{ fontSize: 10, color: '#9b2d7f', fontWeight: 700, background: '#f3e8f0', padding: '1px 5px', borderRadius: 4 }}>
                          rev{q.revision}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleOpen(q)}
                      disabled={loadingId === q.id}
                      style={{
                        background: 'none',
                        border: '1.5px solid #9b2d7f',
                        color: '#9b2d7f',
                        borderRadius: 6,
                        padding: '5px 14px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: loadingId === q.id ? 'not-allowed' : 'pointer',
                        opacity: loadingId === q.id ? 0.5 : 1,
                      }}
                    >
                      {loadingId === q.id ? '...' : 'Open'}
                    </button>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{q.customer_name}</div>
                    {q.address && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{q.address}</div>}
                    <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>{formatDate(q.quote_date)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9b6b8e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Grand Total</span>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(q.grand_total)}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>Pay {formatCurrency(q.to_be_paid)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#9b6b8e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Advance</span>
                      <div
                        onClick={() => !advanceUpdating[q.id] && handleAdvanceToggle(q)}
                        style={{
                          display: 'inline-block',
                          width: 40,
                          height: 22,
                          borderRadius: 11,
                          background: q.advance_paid ? '#9b2d7f' : '#eee',
                          position: 'relative',
                          cursor: advanceUpdating[q.id] ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          opacity: advanceUpdating[q.id] ? 0.6 : 1,
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: 2,
                          left: q.advance_paid ? 20 : 2,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: '#fff',
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                      {q.advance_paid && <span style={{ fontSize: 11, color: '#9b2d7f' }}>Paid</span>}
                      {!q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) && (
                        <span style={{ fontSize: 11, color: '#f0a500' }}>Unpaid</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <select
                        value={q.status}
                        disabled={!!statusUpdating[q.id]}
                        onChange={e => handleStatusChange(q, e.target.value)}
                        style={{
                          width: '100%',
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          padding: '6px 8px',
                          fontSize: 12,
                          fontFamily: 'inherit',
                          background: statusUpdating[q.id] ? '#f5f5f5' : '#fff',
                          color: '#333',
                          cursor: statusUpdating[q.id] ? 'not-allowed' : 'pointer',
                          outline: 'none',
                        }}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {statusError[q.id] && (
                        <div style={{ fontSize: 10, color: '#b91c1c', marginTop: 3 }}>{statusError[q.id]}</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={q.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '170px 1fr 110px 140px 80px 150px 72px',
                    padding: '14px 20px',
                    alignItems: 'center',
                    borderTop: i > 0 ? '1px solid #f5eff3' : 'none',
                    borderLeft: !q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) ? '3px solid #f0a500' : '3px solid transparent',
                    background: !q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) ? '#fff8f0' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#fdf8fc'}
                  onMouseOut={e => e.currentTarget.style.background = !q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) ? '#fff8f0' : 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 700, color: '#2d1a29' }}>
                      {q.display_quote_no}
                    </span>
                    {q.revision > 0 && (
                      <span style={{ fontSize: 10, color: '#9b2d7f', fontWeight: 700, background: '#f3e8f0', padding: '1px 5px', borderRadius: 4 }}>
                        rev{q.revision}
                      </span>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{q.customer_name}</div>
                    {q.address && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{q.address}</div>}
                  </div>
                  <div style={{ fontSize: 13, color: '#777' }}>{formatDate(q.quote_date)}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(q.grand_total)}</div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>Pay {formatCurrency(q.to_be_paid)}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      onClick={() => !advanceUpdating[q.id] && handleAdvanceToggle(q)}
                      style={{
                        display: 'inline-block',
                        width: 40,
                        height: 22,
                        borderRadius: 11,
                        background: q.advance_paid ? '#9b2d7f' : '#eee',
                        position: 'relative',
                        cursor: advanceUpdating[q.id] ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s',
                        opacity: advanceUpdating[q.id] ? 0.6 : 1,
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: 2,
                        left: q.advance_paid ? 20 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                    {q.advance_paid && (
                      <div style={{ fontSize: 11, color: '#9b2d7f', marginTop: 3 }}>Paid</div>
                    )}
                    {!q.advance_paid && ['In Production', 'Ready', 'Delivered'].includes(q.status) && (
                      <div style={{ fontSize: 11, color: '#f0a500', marginTop: 3 }}>Unpaid</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <select
                      value={q.status}
                      disabled={!!statusUpdating[q.id]}
                      onChange={e => handleStatusChange(q, e.target.value)}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        padding: '4px 8px',
                        fontSize: 12,
                        fontFamily: 'inherit',
                        background: statusUpdating[q.id] ? '#f5f5f5' : '#fff',
                        color: '#333',
                        cursor: statusUpdating[q.id] ? 'not-allowed' : 'pointer',
                        outline: 'none',
                      }}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {statusError[q.id] && (
                      <div style={{ fontSize: 10, color: '#b91c1c', marginTop: 3 }}>{statusError[q.id]}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => handleOpen(q)}
                      disabled={loadingId === q.id}
                      style={{
                        background: 'none',
                        border: '1.5px solid #9b2d7f',
                        color: '#9b2d7f',
                        borderRadius: 6,
                        padding: '5px 14px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: loadingId === q.id ? 'not-allowed' : 'pointer',
                        opacity: loadingId === q.id ? 0.5 : 1,
                      }}
                    >
                      {loadingId === q.id ? '...' : 'Open'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
